<template>
  <div class="form-option-element-wrapper">
    <FormLabel
      :element="element"
      :labelStyle="labelStyle"
      :fieldStyle="fieldStyle"
    />
    <FormDescription :element="element" :labelStyle="labelStyle" />
    <!-- Custom Radio Group -->
    <div
      v-if="element.type === 'radio'"
      class="option-wrapper custom-options-group d-flex flex-wrap gap-3 align-items-center justify-content-start"
      :class="{
        'oval-shape': isOvalShapeOption,
        'show-vertical': element?.settings?.showVerticle,
        disabled: element.settings.disabled,
      }"
      :id="`option-element-group-${element.id}`"
    >
      <template v-if="isOvalShapeOption">
        <!-- Oval Shape Radio Buttons -->
        <div
          v-for="(option, index) in element.options"
          :key="option.value + index"
          class="btn"
          :class="{ active: element.value === option.value }"
          :id="`option-${index}-wrap-${element.id}`"
          @click="handleOvalClick(option.value)"
        >
          <input
            type="radio"
            :id="`radio-input-${index}-${element.id}`"
            :name="`radio-group-${element.id}`"
            :value="option.value"
            v-model="element.value"
            :disabled="element.settings.disabled"
          />
          <span :id="`option-${index}-label-${element.id}`">{{
            option.label
          }}</span>
        </div>
      </template>
      <template v-else>
        <!-- Standard Radio Buttons -->
        <div
          v-for="(option, index) in element.options"
          :key="option.value + index"
          class="custom-control custom-radio"
          :id="`option-${index}-wrap-${element.id}`"
        >
          <input
            type="radio"
            :id="`radio-input-${index}-${element.id}`"
            :name="`radio-group-${element.id}`"
            :value="option.value"
            v-model="element.value"
            :disabled="element.settings.disabled"
            class="custom-control-input"
          />
          <label
            :for="`radio-input-${index}-${element.id}`"
            class="custom-control-label"
            :id="`option-${index}-label-${element.id}`"
            @click="preventDefaultIfAskAi"
          >
            {{ option.label }}
          </label>
        </div>
      </template>
    </div>
    <!-- Custom Checkbox Group -->
    <div
      v-if="element.type === 'checkbox'"
      class="option-wrapper custom-options-group d-flex flex-wrap gap-3 align-items-center justify-content-start"
      :class="{
        'oval-shape': isOvalShapeOption,
        'show-vertical': element?.settings?.showVerticle,
        disabled: element.settings.disabled,
      }"
      :id="`option-element-group-${element.id}`"
    >
      <template v-if="isOvalShapeOption">
        <!-- Oval Shape Checkboxes -->
        <div
          v-for="(option, index) in element.options"
          :key="option.value + index"
          class="btn"
          :class="{ active: (element.value || []).includes(option.value) }"
          :id="`option-${index}-wrap-${element.id}`"
          @click="handleOvalClick(option.value)"
        >
          <input
            type="checkbox"
            :id="`checkbox-input-${index}-${element.id}`"
            :value="option.value"
            v-model="element.value"
            :disabled="element.settings.disabled"
          />
          <span :id="`option-${index}-label-${element.id}`">{{
            option.label
          }}</span>
        </div>
      </template>
      <template v-else>
        <!-- Standard Custom Checkboxes -->
        <div
          v-for="(option, index) in element.options"
          :key="option.value + index"
          class="custom-control custom-checkbox"
          :id="`option-${index}-wrap-${element.id}`"
        >
          <input
            type="checkbox"
            :id="`checkbox-input-${index}-${element.id}`"
            :value="option.value"
            v-model="element.value"
            :disabled="element.settings.disabled"
            class="custom-control-input"
          />
          <label
            :for="`checkbox-input-${index}-${element.id}`"
            class="custom-control-label"
            :id="`option-${index}-label-${element.id}`"
            @click="preventDefaultIfAskAi"
          >
            {{ option.label }}
          </label>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';

export default {
  name: 'FormOptionElement',
  data() {
    return {};
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    fieldStyle: {
      type: Object,
      default: () => {},
    },
    labelStyle: {
      type: Object,
      default: () => {},
    },
    brandedStyle: {
      type: Object,
      default: () => {},
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    isAskAiModalVisible: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    handleOvalClick(value) {
      if (this.isAskAiModalVisible) return;
      if (this.element.settings.disabled) return;

      if (this.element.type === 'radio') {
        this.element.value = value;
      } else if (this.element.type === 'checkbox') {
        const arr = Array.isArray(this.element.value) ? this.element.value : [];
        this.element.value = arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value];
      }
    },
    preventDefaultIfAskAi(e) {
      if (this.isAskAiModalVisible) e.preventDefault();
    },
  },
  computed: {
    isOvalShapeOption() {
      return this.element.settings.appearance === 'oval';
    },
    getBrandStyles() {
      const defaults = {
        textColor: '#000000',
      };
      return {
        textColor: this.brandedStyle?.textColor || defaults.textColor,
      };
    },
    isDisabled() {
      return this.element.settings.disabled;
    },
  },
  components: { FormLabel, FormDescription },
};
</script>

<style scoped>
/* shared */
.form-option-element-wrapper .option-wrapper ::v-deep .custom-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-height: unset;
  position: relative;
  padding-left: 0; /* moved to label for reliable hit area */
}

/* label becomes the click target */
.form-option-element-wrapper
  .option-wrapper
  ::v-deep
  .custom-control
  .custom-control-label {
  position: relative; /* was unset, breaks pseudo box targeting */
  display: inline-block;
  padding-left: 33px; /* include the faux box inside label area */
  user-select: none;
  margin: 0;
  cursor: pointer;
  font-family: 'OpenSans-regular';
  font-size: 14px;
  line-height: 19px;
  color: v-bind('getBrandStyles.textColor');
  letter-spacing: -0.02em;
  word-break: break-word;
}

.form-option-element-wrapper
  .option-wrapper
  ::v-deep
  .custom-radio
  .custom-control-label {
  padding-left: 0;
}
/* the hidden input still occupies the box under ::before */
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  .custom-checkbox
  input.custom-control-input {
  position: absolute !important;
  left: 0;
  top: 0;
  width: 19px; /* not 0 */
  height: 19px;
  opacity: 0 !important;
  margin: 0;
  pointer-events: auto;
}
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  .custom-radio
  input.custom-control-input {
  width: 19px; /* not 0 */
  height: 19px;
  accent-color: black;
}

/* custom box */
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  .custom-checkbox
  .custom-control-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 19px;
  height: 19px;
  border: 1px solid #a9a9a9;
  border-radius: 3px; /* radios will still be square per your design */
  overflow: hidden;
  background-color: #fff;
  transition: all 0.2s ease-in-out;
}

/* check icon (checkbox only) */
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  .custom-checkbox
  .custom-control-label::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 2px;
  width: 13px;
  height: 15px;
  background: url(../../../assets/img/checkbox-check.svg) center / 100% 100%
    no-repeat;
  display: none;
}

/* checked states */
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  .custom-checkbox
  input:checked
  ~ .custom-control-label::before {
  background-color: #000000;
  border-color: #000000;
}
.form-option-element-wrapper
  ::v-deep
  .custom-options-group
  input:checked
  ~ .custom-control-label::after {
  display: block;
}

/* disabled */
.form-option-element-wrapper
  ::v-deep
  .custom-options-group.disabled
  input
  ~ .custom-control-label::before {
  border-color: #aba9a9 !important;
  cursor: not-allowed !important;
}
.form-option-element-wrapper
  ::v-deep
  .custom-options-group.disabled
  input:checked
  ~ .custom-control-label::before {
  background-color: #aba9a9 !important;
}
.form-option-element-wrapper
  ::v-deep
  .custom-options-group.disabled
  input
  ~ .custom-control-label {
  color: #c7c7c7 !important;
  cursor: not-allowed !important;
}
.form-option-element-wrapper ::v-deep .oval-shape.disabled .btn {
  border-color: #aba9a9 !important;
  cursor: not-allowed !important;
  color: #c7c7c7 !important;
}
.form-option-element-wrapper ::v-deep .oval-shape.disabled .btn.active {
  background-color: #aba9a9 !important;
}

/* group spacing */
.form-option-element-wrapper ::v-deep .custom-options-group {
  row-gap: 21px !important;
}
.form-option-element-wrapper .custom-options-group .custom-control-label {
  cursor: pointer;
}

/* vertical layout */
.form-option-element-wrapper .option-wrapper.show-vertical {
  display: flex;
  align-items: start !important;
  flex-direction: column !important;
}

/* OVAL STYLE */
.form-option-element-wrapper .option-wrapper.oval-shape ::v-deep .btn {
  border-radius: 30px;
  width: 130px;
  max-width: 130px;
  min-height: 55px;
  padding: 10px;
  border: 1px solid #d6d8d9;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  font-family: 'OpenSans-regular';
  font-size: 14px;
  letter-spacing: -0.02em;
  line-height: 27px;
  color: #222;
  transition: transform 0.3s ease;
  word-break: break-word;
  cursor: pointer;
  margin: 0;
  position: relative;
}
.form-option-element-wrapper .option-wrapper.oval-shape ::v-deep .btn:hover {
  border-color: #000;
  transition: 0.2s ease all;
}
.form-option-element-wrapper .option-wrapper.oval-shape ::v-deep .btn.active {
  background-color: #000;
  border-color: #a9a9a9;
  color: #fff;
}
.form-option-element-wrapper .option-wrapper.oval-shape ::v-deep .btn:active {
  transform: scale(0.9);
}
.form-option-element-wrapper .option-wrapper.oval-shape ::v-deep .btn input {
  display: none;
}

/* oval disabled state */
.form-option-element-wrapper
  .option-wrapper.oval-shape.disabled
  .custom-control {
  cursor: not-allowed;
  opacity: 0.6;
}
.form-option-element-wrapper
  .option-wrapper.oval-shape.disabled
  .custom-control
  label {
  cursor: not-allowed;
}
</style>
